import { Localized } from "@fluent/react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import {
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle
} from "@mui/material";
import { useState } from "react";
import { AppId } from "../../../../app/appTypes";
import { SaveButton } from "../../../../app/components/SaveButton";
import { showError, showSuccess } from "../../../notifications/notificationsSlice";
import { CancelButton } from "../../../../app/components/CancelButton";
import { alpha } from "@mui/material/styles";
import { prepareValidationStage, PrepareValidationStagePayload, ProcessStageOverview } from "../validationProcessApi";
import { selectAllUsers } from "../../../users/usersSlice";
import React from "react";
import DocumentTargetInfo from "./DocumentTargetInfo";
import { DocumentSelectField } from "../../../documents/controls/DocumentSelectField";
import { DocumentHeader } from "../../../documents/documentsApi";

type UserOption = {
    userName: string;
    firstname: string;
    surname: string;
    email: string;
    position?: string;
};


export interface URSStageActivationProps {
    stageCode: "URS";
    processId: AppId | null;
    structureId: number;
    processName?: string;
    processNumber?: string;
    deviceId?: string;

    onSuccess: (stage: ProcessStageOverview) => void;
    onCancel: () => void;
}

export const URSStageActivation: React.FC<URSStageActivationProps> = (props) => {
    const dispatch = useAppDispatch();

    const allUsers = useAppSelector(selectAllUsers);

    const users: UserOption[] = React.useMemo(
        () =>
            (allUsers ?? []).map((u: any) => ({
                userName: String(u.userName),
                firstname: u.firstName ?? u.firstname ?? "",
                surname: u.lastName ?? u.surname ?? "",
                email: u.email ?? "",
                position: u.position,
            })),
        [allUsers]
    );

    const [selectedUrsId, setSelectedUrsId] = useState<string | null>(null);
    const [selectedUrsDoc, setSelectedUrsDoc] = useState<DocumentHeader | null>(null);
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");

    const isBasicsOk =
        !!selectedUrsId &&
        !!props.processId;

    const isFormValid = isBasicsOk;

    const handleSaveClick = async () => {
        if (!isFormValid) {
            setServerError("fill-all-required-fields");
            return;
        }

        const payload: PrepareValidationStagePayload = {
            stageCode: "URS",
            processId: props.processId!,
            documents: [],
            toLink: selectedUrsId ? [{ type: "URS", documentId: Number(selectedUrsId) }] : [],
            removeExistingLinks: true
        };

        setSaving(true);
        setServerError("");
        try {
            const result = await dispatch(prepareValidationStage(payload));

            if (prepareValidationStage.fulfilled.match(result)) {
                const payload = result.payload as ProcessStageOverview;
                dispatch(showSuccess("saved"));
                props.onSuccess(payload);
            } else {
                let errorMsg = "error";
                if (result.payload && result.payload.kind === "http") {
                    const problem = result.payload.problem;
                    if (problem) {
                        errorMsg = problem.title;
                    }
                }
                setServerError(errorMsg);
            }

        } catch (e) {
            setServerError("error");
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };
    return (
        <Dialog open fullWidth maxWidth="md"
            slotProps={{
                backdrop: {
                    sx: {
                        bgcolor: (t) => alpha(t.palette.grey[900], 0.5),
                    },
                },
            }}
        >
            <DialogTitle sx={{ fontWeight: 700 }}>
                <Localized id="urs-stage-activation-title">
                    <span>Dodawanie dokumentu URS</span>
                </Localized>
            </DialogTitle>

            <DialogContent sx={{ pt: 1 }}>
                <DocumentTargetInfo
                    objectId={props.deviceId}
                    objectType="DEVICE"
                    processNumber={props.processNumber}
                    processTitle={props.processName}
                    structureId={props.structureId}
                />

                <DocumentSelectField
                    documentType="URS"
                    value={selectedUrsId}
                    selected={selectedUrsDoc}
                    filterStructureId={props.structureId != null ? String(props.structureId) : null}
                    defaultStatusFilter={["A", "P"]}
                    onChange={(id, doc) => {
                        setSelectedUrsId(id);
                        setSelectedUrsDoc(doc ?? null);
                    }}
                />

                {serverError && (
                    <Alert sx={{ mt: 2 }} onClose={() => setServerError("")} severity="error">
                        <Localized id={serverError}><span>Server error</span></Localized>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <SaveButton loading={saving} onClick={handleSaveClick} />
                <CancelButton loading={saving} onClick={props.onCancel} />
            </DialogActions>
        </Dialog >
    );
};
